import { useMemo, useRef, type CSSProperties } from 'react';
import { marked } from 'marked';
import { escapeLoneTildes } from '~/lib/markdown-tildes';
import { highlightHtml } from '~/lib/highlight-html';
import { useAdaptiveTables } from './use-adaptive-tables';

/**
 * Elements that must never reach the DOM from rendered Markdown, matched with
 * their content so a `<script>`/`<style>` body doesn't survive as loose text.
 */
const DANGEROUS_BLOCK =
  /<(script|style|iframe|object|embed|form|noscript)\b[\s\S]*?<\/\1\s*>/gi;
/** The same elements as lone/unclosed tags, plus the document-level ones. */
const DANGEROUS_TAG =
  /<\/?(?:script|style|iframe|object|embed|form|noscript|link|meta|base|input|button|textarea|select)\b[^>]*>/gi;

/** Re-apply a replacement until it stops changing the string. */
function repeatUntilStable(value: string, apply: (s: string) => string): string {
  let current = value;
  // Bounded so a pathological input can't spin: each pass must shrink the
  // string to continue, and 20 nested reassemblies is far past anything real.
  for (let i = 0; i < 20; i++) {
    const next = apply(current);
    if (next === current) return current;
    current = next;
  }
  return current;
}

/**
 * Strip active content from rendered Markdown.
 *
 * `marked` passes raw HTML through by design, and the Markdown we render is
 * model-authored (chat replies, cron briefings, documents Eddy wrote from web
 * content), so a prompt-injected `<iframe src="javascript:…">` would otherwise
 * execute in our own origin. Removal is applied REPEATEDLY: a single pass lets
 * the neighbours of a removed tag re-join into a fresh one (`<if<x>rame …>`).
 */
function sanitize(html: string): string {
  const stripped = repeatUntilStable(html, (s) =>
    s.replace(DANGEROUS_BLOCK, '').replace(DANGEROUS_TAG, ''),
  );
  return stripped
    .replace(/\bon\w+\s*=/gi, 'data-blocked=')
    .replace(/\b(href|src|xlink:href)\s*=\s*(["']?)\s*javascript:/gi, '$1=$2blocked:');
}

/** Every anchor OPEN tag in the rendered output, in any casing — both the ones `marked` builds from Markdown link syntax AND a raw `<a>` tag it merely passes through unparsed. Attrs may span lines (`[^>]*` matches `\n` too, so no `s` flag is needed). */
const ANCHOR_OPEN_TAG = /<a\b[^>]*>/gi;

/** An anchor with more attribute text than this isn't legitimate content — bail before doing any further work on it (see `forceNewTabLinks`). */
const MAX_ANCHOR_TAG_LENGTH = 10_000;

/**
 * Pull an anchor tag's `href` value out, in any of its quoting forms. `null`
 * when there is no href at all.
 *
 * The boundary before `href` is `(?:^|[\s"'])`, not `\b` — `\b` also matches
 * right after a hyphen, so `<a data-href="#x" href="…">` would read
 * `data-href`'s value (a common placeholder on real pages) and never reach
 * the real `href`.
 */
function anchorHref(tag: string): string | null {
  const match = tag.match(/(?:^|[\s"'])href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/i);
  if (!match) return null;
  return match[1] ?? match[2] ?? match[3] ?? '';
}

/**
 * FRONT-1436: force every rendered anchor to open in a new tab.
 *
 * This is a post-pass over the SANITIZED html rather than a `marked` renderer
 * override, on purpose: a renderer override only ever sees a Markdown-syntax
 * link ([text](url), a reference link, an autolink). `marked` passes raw HTML
 * straight through unparsed, so a raw `<a href="…">` a model wrote directly
 * into a reply or a saved web document — the exact content `sanitize()`
 * exists for — would sail past a renderer override untouched. Rewriting the
 * final string catches every anchor regardless of how it was authored.
 *
 * A fragment link (`#section`) or an empty href is left byte-identical: it
 * never leaves the page, and forcing `target="_blank"` on it would open a
 * second copy of the app instead of scrolling.
 *
 * Any existing `target`/`rel` on the tag is dropped and replaced, not merged:
 * model content has no claim to `target="_self"`. Routing every surviving
 * link through a fresh, opener-less browsing context also helps against an
 * HTML-entity-encoded `javascript:` href (`&#106;avascript:…`) —
 * `sanitize()`'s literal-spelling rewrite can't see that form, since the
 * entities only decode into a live URL once the *browser* parses the
 * attribute, after this regex has already run. Whether that navigation then
 * actually gets blocked is the browser's own handling of a `javascript:` URL
 * opened into a fresh, opener-less context — behavior we're leaning on, not a
 * guarantee this code makes.
 */
function forceNewTabLinks(html: string): string {
  return html.replace(ANCHOR_OPEN_TAG, (tag) => {
    // A real anchor never carries anywhere near this much attribute text.
    // Bail before doing any further work: untouched means old same-tab
    // behavior for this one tag, which is cheaper and safer than risking the
    // quadratic backtracking the strip step below would otherwise hit on
    // adversarial padding.
    if (tag.length > MAX_ANCHOR_TAG_LENGTH) return tag;

    // An odd count of DOUBLE quotes means the `[^>]*` match above was
    // truncated INSIDE a double-quoted attribute value (`title="a > b"` has
    // a `>` that isn't the tag's real close), so this string isn't the whole
    // tag we think it is. Splicing attributes into it would corrupt the
    // value and leak stray text into the message — bail instead.
    //
    // Single quotes are DELIBERATELY not counted the same way: `marked`
    // doesn't escape an apostrophe inside an href/title it builds
    // (`https://en.wikipedia.org/wiki/O'Hare_International_Airport` is
    // ordinary, valid content), so counting them produced false bails —
    // same-tab links surviving on common URLs and titles — for content that
    // was never truncated at all. The accepted trade-off: a SINGLE-quoted
    // raw-HTML attribute containing its own ">" (an `<a href='...'
    // title='a > b'>` a model wrote directly) is a known cosmetic
    // limitation, in the same class as an anchor nested inside ANOTHER
    // tag's own attribute value (an `<img alt="…<a href=…>…">`) — a
    // regex-level pass isn't a full HTML parser either way, and
    // `sanitize()` upstream still guarantees no active content survives
    // regardless.
    const doubleQuotes = (tag.match(/"/g) ?? []).length;
    if (doubleQuotes % 2 !== 0) return tag;

    const href = anchorHref(tag);
    if (href === null || href === '' || href.startsWith('#')) return tag;

    // A single captured boundary character (whitespace or a quote) in place
    // of `\s+` before the literal avoids catastrophic-adjacent quadratic
    // backtracking over a long run of whitespace inside the tag (measured:
    // ~4.4s at 60k padding characters, ~49s at 200k — synchronously, inside
    // the render memo). A fixed-width match can't backtrack the way `\s+`
    // does. The captured character is re-emitted so stripping "target=…"
    // doesn't also eat the separator before the next attribute. Allowing a
    // quote as the boundary (not just whitespace) also handles minified HTML
    // with no space at all before the attribute, e.g. `href="x"target="_self"`.
    const stripped = tag
      .replace(/([\s"'])target\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'>]+)/gi, '$1')
      .replace(/([\s"'])rel\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'>]+)/gi, '$1');

    // Only treat a trailing "/" as a self-closing marker when it's cleanly
    // separated from whatever came before (preceded by whitespace or a
    // quote) — otherwise it's the last character of an UNQUOTED href's own
    // value (`href=https://x.test/path/>`), and stripping it would silently
    // change the URL. The boundary character is captured and re-emitted
    // (like the target/rel strip above), not swallowed: a non-capturing
    // version would delete a QUOTE boundary along with the slash —
    // `href="x"/>` would lose its closing quote and corrupt the href into a
    // garbage value with no working target at all, strictly worse than
    // before this fix.
    return stripped
      .replace(/([\s"'])\/>$/, '$1>')
      .replace(/>$/, ' target="_blank" rel="noopener noreferrer">');
  });
}

export function MarkdownRenderer({
  content,
  className = '',
  /**
   * Render a single newline as a line break. On for surfaces whose content is
   * often plain text rather than authored Markdown (a cron result), where
   * collapsing the lines into one paragraph loses real structure.
   */
  breaks = false,
  style,
  /**
   * FRONT-1642: a search term to highlight (wrapped in `<mark>`) wherever it
   * appears in the rendered content — used by the Files detail page when it's
   * opened from a command-palette search hit, so the passage that matched is
   * visible rather than buried in a wall of text. Applied AFTER sanitizing
   * and link-forcing, via `highlightHtml` (`~/lib/highlight-html`), which
   * only ever rewrites text between tags. Absent/empty is a no-op — the
   * rendered output is byte-identical to before this prop existed.
   */
  highlightQuery,
}: {
  content: string;
  className?: string;
  breaks?: boolean;
  style?: CSSProperties;
  highlightQuery?: string;
}) {
  const html = useMemo(() => {
    // FRONT-1346: escape lone tildes first — "~1h … ~€50" used to strike
    //   everything between the two approximations.
    const rendered = forceNewTabLinks(
      sanitize(marked.parse(escapeLoneTildes(content), { async: false, breaks }) as string),
    );
    return highlightQuery ? highlightHtml(rendered, highlightQuery) : rendered;
  }, [content, breaks, highlightQuery]);
  // React 19 diffs a host prop by identity, so a fresh `{ __html }` object on
  // every render re-sets innerHTML on EVERY parent re-render, even with the
  // same string — throwing away anything done to the rendered DOM since: a
  // table's measured layout, its sideways scroll position, the Copy table
  // confirmation (FRONT-2539, measured on the stack: a click elsewhere in the
  // chat window rebuilt both replies). One object per distinct html.
  const inner = useMemo(() => ({ __html: html }), [html]);
  const root = useRef<HTMLDivElement>(null);
  // FRONT-2539: tables adapt to their width. This works on the rendered DOM
  //   after the sanitized html is in place and never edits the html string,
  //   so nothing it adds passes around the sanitizer.
  useAdaptiveTables(root, html);
  return (
    <div
      ref={root}
      className={`markdown-body max-w-none ${className}`}
      style={style}
      dangerouslySetInnerHTML={inner}
    />
  );
}
